import React, { useState, useEffect } from 'react';
import { 
  MapPin, Clock, CheckCircle, 
  AlertTriangle, Calendar, Navigation,
  Phone, User, Package, Wrench
} from 'lucide-react';
import api from '../../services/api';
import { format, isToday, isTomorrow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { authStore } from '../../stores/authStore';
import toast from 'react-hot-toast';

const TecnicoDashboard = () => {
  const navigate = useNavigate();
  const { user } = authStore();
  const [loading, setLoading] = useState(true);
  const [ordensServico, setOrdensServico] = useState([]);
  const [ordensHoje, setOrdensHoje] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    finalizadas: 0,
    pendentes: 0,
    emAndamento: 0
  });
  const [selectedOrdem, setSelectedOrdem] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Busca ordens de serviço do técnico
      const hoje = format(new Date(), 'yyyy-MM-dd');
      const [ordensRes, ordensHojeRes] = await Promise.all([
        api.get(`/ordens-servico?page=1&limit=20`),
        api.get(`/ordens-servico/tecnico/${user.id}?data=${hoje}`)
      ]);

      // Filtra apenas as ordens do técnico logado
      const minhasOrdens = ordensRes.data.data?.filter(o => o.tecnicoId === user.id) || [];
      setOrdensServico(minhasOrdens);
      setOrdensHoje(ordensHojeRes.data || []);

      // Calcula estatísticas
      const stats = {
        total: minhasOrdens.length,
        finalizadas: minhasOrdens.filter(o => o.status === 'finalizada').length,
        pendentes: minhasOrdens.filter(o => o.status === 'agendada').length,
        emAndamento: minhasOrdens.filter(o => o.status === 'em_andamento').length
      };
      setEstatisticas(stats);

    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [user.id]);

  const iniciarOrdem = async (ordemId) => {
    try {
      await api.post(`/ordens-servico/${ordemId}/iniciar`, {
        observacoes: 'Ordem iniciada via dashboard'
      });
      toast.success('Ordem de serviço iniciada');
      fetchData();
    } catch (error) {
      toast.error('Erro ao iniciar ordem');
    }
  };

  const finalizarOrdem = (ordemId) => {
    navigate(`/ordens-servico/${ordemId}/finalizar`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'agendada': return 'bg-blue-100 text-blue-800';
      case 'em_andamento': return 'bg-yellow-100 text-yellow-800';
      case 'finalizada': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrioridadeColor = (prioridade) => {
    switch (prioridade) {
      case 'urgente': return 'text-red-600';
      case 'alta': return 'text-orange-600';
      case 'normal': return 'text-blue-600';
      case 'baixa': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard Técnico
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
        >
          <Wrench className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total de O.S.</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estatisticas.total}
              </p>
            </div>
            <Wrench className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pendentes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estatisticas.pendentes}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Em Andamento</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estatisticas.emAndamento}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Finalizadas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estatisticas.finalizadas}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Ordens de Serviço do Dia */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Ordens de Serviço - Hoje
          </h2>
        </div>
        <div className="p-6">
          {ordensHoje.length > 0 ? (
            <div className="space-y-4">
              {ordensHoje.map((ordem) => (
                <div 
                  key={ordem.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold text-gray-600">
                          #{ordem.protocolo}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(ordem.status)}`}>
                          {ordem.status}
                        </span>
                        <span className={`text-sm font-medium ${getPrioridadeColor(ordem.prioridade)}`}>
                          {ordem.prioridade}
                        </span>
                      </div>
                      
                      <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                        {ordem.titulo}
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">
                            <User className="inline w-4 h-4 mr-1" />
                            Cliente: {ordem.cliente?.nome}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400 mt-1">
                            <Phone className="inline w-4 h-4 mr-1" />
                            {ordem.cliente?.telefone}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">
                            <MapPin className="inline w-4 h-4 mr-1" />
                            {ordem.endereco?.logradouro}, {ordem.endereco?.numero}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400 mt-1">
                            {ordem.endereco?.bairro}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          Agendado: {format(new Date(ordem.dataAgendamento), 'HH:mm')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      {ordem.status === 'agendada' && (
                        <button
                          onClick={() => iniciarOrdem(ordem.id)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          Iniciar
                        </button>
                      )}
                      {ordem.status === 'em_andamento' && (
                        <button
                          onClick={() => finalizarOrdem(ordem.id)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          Finalizar
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/ordens-servico/${ordem.id}`)}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                      >
                        Detalhes
                      </button>
                      <button
                        onClick={() => window.open(`https://maps.google.com/?q=${ordem.endereco?.logradouro} ${ordem.endereco?.numero} ${ordem.endereco?.bairro}`, '_blank')}
                        className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                      >
                        <Navigation className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Nenhuma ordem de serviço agendada para hoje
            </p>
          )}
        </div>
      </div>

      {/* Próximas Ordens */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Próximas Ordens de Serviço
          </h2>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 text-sm font-medium text-gray-700 dark:text-gray-300">Protocolo</th>
                  <th className="text-left p-2 text-sm font-medium text-gray-700 dark:text-gray-300">Cliente</th>
                  <th className="text-left p-2 text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</th>
                  <th className="text-left p-2 text-sm font-medium text-gray-700 dark:text-gray-300">Data</th>
                  <th className="text-left p-2 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                  <th className="text-left p-2 text-sm font-medium text-gray-700 dark:text-gray-300">Ações</th>
                </tr>
              </thead>
              <tbody>
                {ordensServico
                  .filter(o => o.status !== 'finalizada')
                  .slice(0, 10)
                  .map((ordem) => (
                    <tr key={ordem.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="p-2 text-sm">{ordem.protocolo}</td>
                      <td className="p-2 text-sm">{ordem.cliente?.nome}</td>
                      <td className="p-2 text-sm">{ordem.tipo}</td>
                      <td className="p-2 text-sm">
                        {format(new Date(ordem.dataAgendamento), 'dd/MM HH:mm')}
                      </td>
                      <td className="p-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(ordem.status)}`}>
                          {ordem.status}
                        </span>
                      </td>
                      <td className="p-2">
                        <button
                          onClick={() => navigate(`/ordens-servico/${ordem.id}`)}
                          className="text-primary-600 hover:text-primary-800 text-sm"
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TecnicoDashboard;